import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { adminFetch } from '@/utils/adminAuth';
import { invalidateHomepageConfig, useHomepageConfig } from '@/hooks/useHomepageConfig';
import { useAuth } from '@/context/AuthContext';

const EditModeContext = createContext(null);

// Deep get/set helpers so we can address values by "hero.badge" or "threeColumns.1.title"
const getAt = (obj, path) => {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, part) => {
    if (acc === undefined || acc === null) return acc;
    if (Array.isArray(acc)) return acc[Number(part)];
    return acc[part];
  }, obj);
};

const setAt = (obj, path, value) => {
  const parts = path.split('.');
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    const next = Array.isArray(cur[p]) ? [...cur[p]] : { ...(cur[p] || {}) };
    cur[p] = next;
    cur = next;
  }
  cur[parts[parts.length - 1]] = value;
  return clone;
};

export const EditModeProvider = ({ children }) => {
  const [enabled, setEnabled] = useState(false);      // is edit-mode active
  const [draft, setDraft] = useState(null);           // draft config being edited
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isAdmin } = useAuth();
  const published = useHomepageConfig();               // saved config for view-mode reads

  const isAdminUser = typeof isAdmin === 'function' ? isAdmin() : false;

  // Warn if leaving with unsaved changes
  useEffect(() => {
    if (!dirty) return;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [dirty]);

  // While edit mode is active, block <a> navigation so clicking inside an
  // editable text that happens to sit inside a <Link> keeps the caret on the
  // page instead of navigating away. We call preventDefault (not
  // stopPropagation) so React Router's Link onClick sees defaultPrevented and
  // skips its own history.push, while contentEditable still receives focus
  // via the native mousedown/click. Admin controls opt-out with
  // `data-edit-safe`.
  useEffect(() => {
    if (!enabled) return undefined;
    document.body.classList.add('cs-editing');
    const clickCapture = (e) => {
      const t = e.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('[data-edit-safe]')) return;
      const anchor = t.closest('a');
      if (anchor) {
        e.preventDefault();
      }
    };
    document.addEventListener('click', clickCapture, true);
    return () => {
      document.body.classList.remove('cs-editing');
      document.removeEventListener('click', clickCapture, true);
    };
  }, [enabled]);

  const enter = useCallback(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-config`);
      const data = await r.json();
      setDraft(data);
      setDirty(false);
      setEnabled(true);
    } catch (e) {
      toast.error('Failed to enter edit mode');
    }
  }, []);

  const cancel = useCallback(() => {
    if (dirty && !window.confirm('Discard your unsaved changes?')) return;
    setEnabled(false);
    setDraft(null);
    setDirty(false);
  }, [dirty]);

  const update = useCallback((path, value) => {
    setDraft(prev => (prev ? setAt(prev, path, value) : prev));
    setDirty(true);
  }, []);

  const save = useCallback(async () => {
    if (!draft) return;
    setSaving(true);
    try {
      const res = await adminFetch('/admin/homepage-config', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hero: draft.hero,
          pillars: draft.pillars,
          performancesSection: draft.performancesSection,
          testimonialsHeading: draft.testimonialsHeading,
          clientsHeading: draft.clientsHeading,
          clientsSubheading: draft.clientsSubheading,
          clients: draft.clients,
          featuredVideo: draft.featuredVideo,
          threeColumns: draft.threeColumns,
          whyChooseUs: draft.whyChooseUs,
          testimonials: draft.testimonials,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      invalidateHomepageConfig();
      toast.success('Saved!');
      setDirty(false);
      setEnabled(false);
      // Force a reload so the public site refreshes with cached-invalidated data
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [draft]);

  const value = {
    enabled,
    draft,
    dirty,
    saving,
    isAdminUser,
    enter,
    cancel,
    save,
    update,
    getValue: (path) => {
      // In edit mode use the draft; otherwise fall back to the saved config so
      // components render whatever the admin persisted, not the hardcoded fallback.
      const source = draft || published;
      return source ? getAt(source, path) : undefined;
    },
  };

  return <EditModeContext.Provider value={value}>{children}</EditModeContext.Provider>;
};

export const useEditMode = () => {
  const ctx = useContext(EditModeContext);
  if (!ctx) {
    return { enabled: false, isAdminUser: false, getValue: () => undefined, update: () => {}, enter: () => {}, cancel: () => {}, save: () => {} };
  }
  return ctx;
};
